import { Clock, Database, Filter, Mail, Shield, Send, Workflow } from 'lucide-react';

const Features = () => {
  const topRowFeatures = [
    {
      icon: Clock,
      title: "Save Valuable Time",
      description: "Automatically process scouting emails and organize player data, saving hours of administrative work every week.",
      gradient: "from-cyan-500 to-teal-500",
      delay: "0s"
    },
    {
      icon: Database,
      title: "Centralized Database",
      description: "Keep all player information, videos, and communications in one searchable, filterable database inspired by Football Manager.",
      gradient: "from-teal-500 to-emerald-500",
      delay: "0.1s"
    },
    {
      icon: Filter,
      title: "Smart Shortlists",
      description: "Create custom shortlists to organize top prospects by position, skill level, or any criteria that matters to your program.",
      gradient: "from-emerald-500 to-green-500",
      delay: "0.2s"
    }
  ];

  const bottomRowFeatures = [
    {
      icon: Mail,
      title: "AI Email Processing",
      description: "Our AI automatically extracts and categorizes player information from recruitment emails with 99% accuracy.",
      gradient: "from-cyan-500 to-blue-500",
      delay: "0.3s"
    },
    {
      icon: Shield,
      title: "Secure & Compliant",
      description: "Enterprise-grade security with FERPA compliance to protect student-athlete information and maintain privacy.",
      gradient: "from-emerald-500 to-teal-500",
      delay: "0.4s"
    },
    {
      icon: Send,
      title: "Email Campaigns & ID Camp Reach-outs",
      description: "Send personalized mass email campaigns and ID camp invitations directly from Bloom, streamlining your outreach to prospects.",
      gradient: "from-blue-500 to-indigo-500",
      delay: "0.5s"
    },
    {
      icon: Workflow,
      title: "Seamless Integration",
      description: "Effortlessly integrate with your existing workflow through powerful export options and direct email connectivity that eliminates data silos.",
      gradient: "from-indigo-500 to-purple-500",
      delay: "0.6s"
    }
  ];

  return (
    <section id="features" className="py-24 bg-gradient-to-br from-white via-gray-50 to-cyan-50 relative overflow-hidden">
      {/* Enhanced background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Flowing gradient shapes */}
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-30"
          style={{
            background: `
              radial-gradient(circle at 15% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 40%),
              radial-gradient(circle at 85% 80%, rgba(20, 184, 166, 0.1) 0%, transparent 40%),
              radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)
            `
          }}
        />
        
        {/* Organic gradient blobs */}
        <div className="absolute top-20 left-10 w-64 h-64 opacity-20">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: `
                radial-gradient(ellipse 70% 80% at 30% 40%, rgba(6, 182, 212, 0.3) 0%, transparent 70%)
              `,
              filter: 'blur(40px)'
            }}
          />
        </div>
        
        <div className="absolute bottom-20 right-10 w-80 h-80 opacity-15">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: `
                radial-gradient(ellipse 80% 60% at 70% 60%, rgba(16, 185, 129, 0.4) 0%, transparent 70%)
              `,
              filter: 'blur(50px)'
            }}
          />
        </div>

        {/* Subtle mesh gradient */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.8) 0%, transparent 25%),
              radial-gradient(circle at 80% 80%, rgba(20, 184, 166, 0.8) 0%, transparent 25%),
              radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.8) 0%, transparent 25%),
              radial-gradient(circle at 20% 80%, rgba(6, 182, 212, 0.8) 0%, transparent 25%)
            `
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why Coaches Choose{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
              Bloom
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our platform automates the time-consuming parts of recruiting so you can focus on what matters: building your team.
          </p>
        </div>

        {/* Top row - 3 cards */}
        <div className="flex flex-wrap justify-center gap-8 max-w-6xl mx-auto mb-12">
          {topRowFeatures.map((feature, index) => (
            <div 
              key={index}
              className="w-full md:w-[calc(50%-1rem)] lg:w-[300px] group p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 hover:border-cyan-200 relative overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-transparent to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Glowing border effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-teal-400/20 to-emerald-400/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className={`relative w-14 h-14 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500`}>
                <feature.icon className="w-7 h-7 text-white" />
                {/* Icon glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-xl blur opacity-0 group-hover:opacity-60 transition-opacity duration-500`}></div>
              </div>
              
              <h3 className="relative text-xl font-bold text-gray-900 mb-4 group-hover:text-cyan-600 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="relative text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                {feature.description}
              </p>

              {/* Subtle floating particles */}
              <div className="absolute top-4 right-4 w-2 h-2 bg-cyan-400/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute bottom-4 left-4 w-1 h-1 bg-teal-400/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            </div>
          ))}
        </div>

        {/* Bottom row - 4 cards */}
        <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto">
          {bottomRowFeatures.map((feature, index) => (
            <div 
              key={index}
              className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)] xl:w-[280px] group p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 hover:border-cyan-200 relative overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-transparent to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Glowing border effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-teal-400/20 to-emerald-400/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className={`relative w-14 h-14 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500`}>
                <feature.icon className="w-7 h-7 text-white" />
                {/* Icon glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-xl blur opacity-0 group-hover:opacity-60 transition-opacity duration-500`}></div>
              </div>
              
              <h3 className="relative text-xl font-bold text-gray-900 mb-4 group-hover:text-cyan-600 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="relative text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                {feature.description}
              </p>

              {/* Subtle floating particles */}
              <div className="absolute top-4 right-4 w-2 h-2 bg-cyan-400/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute bottom-4 left-4 w-1 h-1 bg-teal-400/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features; 